import {
  ASSIGNED_CONTACT_ID,
  DOCUMENT_SENDER_ID,
  DefaultValidationObjectForTemplate,
  DiscountType,
  ELEMENTS_LOOKUP,
  IElement,
  IPricingTable,
  LineItem,
  PriceTypeEnum,
  ProductListElementOptions,
  RecipientEntityTypeEnum,
  TemplateTypeEnum,
  UpdateDocument,
  calculator,
  convertObjToSnakeCase,
  loadFonts,
  normalizeRruleForV2Form,
  setFillableFieldsDataIntoDocument,
  validateDocumentSaveCardDetails,
} from '@gohighlevel/ghl-proposals-common'
import { computed, ref } from 'vue'

import { DocumentServices } from '@/service/DocumentService'
import { useAppStore } from '@/store/app'
import { useBuilderStore } from '@/store/builder'
import { useProductListStore } from '@/store/productListStore'
import { getTimeZoneAbbreviation } from '@/util'
import { AxiosError } from 'axios'
import { union } from 'lodash'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useCurrency } from './useCurrency'
import { useHtmlToCanvas } from './useHtmlToCanvas'
import { useLineItems } from './useLineItems'
import { useNotify } from './useNotify'
import { usePostmate } from './usePostmate'

const loading = ref(false)
const loadingCustomValues = ref(false)
const publishingDocument = ref(false)
const contentLibraryCustomContentCount = ref(0)

export const useDocument = () => {
  const route = useRoute()
  const { t, locale } = useI18n()
  const appStore = useAppStore()
  const builderStore = useBuilderStore()

  const productListStore = useProductListStore()
  const documentVersion = computed(() => appStore.document?.version ?? 1)
  // const recipients = computed(() => appStore.document.recipients)
  const { getCurrency } = useCurrency()
  const { handleError, handleSuccess } = useNotify()
  const { redirectOnParentRoute } = usePostmate()
  const { generateThumbnailForPage } = useHtmlToCanvas()

  const validateDocument = (options?: { skipSaveCardValidations?: boolean }) => {
    const errors = DefaultValidationObjectForTemplate
    const productListErrors = productListStore.getProductListErrors()

    const settingsErrors = appStore.getSettingsErrors()
    errors.isNotificationSettingsValid = settingsErrors && settingsErrors.length > 0 ? false : true

    const redirectUrlErrors = appStore.getRedirectUrlSettingErrors()
    errors.isRedirectUrlSettingsValid = redirectUrlErrors && redirectUrlErrors.length > 0 ? false : true

    const customValueLinkageErrors = builderStore.getCustomValueLinkageErrors()
    errors.isCustomValueLinkageValid = customValueLinkageErrors && customValueLinkageErrors.length > 0 ? false : true

    // Document name validation - disallow raw double-quote (")
    const docName = (appStore.document?.name ?? '').toString()
    const hasDoubleQuote = docName.includes('"')

    //document in published state should not show document name and productList validation
    if (appStore.document?.isPublished) {
      errors.isDocumentNameValid = true
      errors.isProductListValid = true
    } else {
      // for non-published documents, apply the validation
      errors.isDocumentNameValid = !hasDoubleQuote
      errors.isProductListValid = productListErrors && productListErrors.length > 0 ? false : true
    }

    const imageSizeErrors = builderStore.getImageSizeErrors()
    errors.isImageSizeValid = imageSizeErrors && imageSizeErrors.length > 0 ? false : true

    if (options?.skipSaveCardValidations) {
      errors.isPaymentProviderConfigured = true
      errors.isPaymentProviderSupportedForSaveCard = true
      errors.isSaveCardDetailsAssignedToContact = true
    } else {
      const saveCardDetailsValidation = validateDocumentSaveCardDetails(
        builderStore.pages as any,
        appStore.paymentIntegrations,
        appStore.document?.paymentLiveMode ?? true,
        appStore.document?.recipients ?? []
      )
      errors.isPaymentProviderConfigured = saveCardDetailsValidation.isPaymentProviderConfigured
      errors.isPaymentProviderSupportedForSaveCard = saveCardDetailsValidation.isPaymentProviderSupportedForSaveCard
      errors.isSaveCardDetailsAssignedToContact = saveCardDetailsValidation.isSaveCardDetailsAssignedToContact
    }

    return errors
  }

  const loadDocument = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.getDocument({
        locationId: route.params.locationId as string,
        id: route.params.id as string,
      })
      if (data) setStore(data)
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  const loadTemplate = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.getPublicTemplate({
        id: route.params.id as string,
      })
      if (data) setStore(data)
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  const updatePagesAfterUpload = async () => {
    try {
      const { data } = await DocumentServices.getPublicTemplate({
        id: route.params.id as string,
      })

      // Only process and update pages
      const pages = setFillableFieldsDataIntoDocument(data.pages, data.fillableFields, data.pricingTables)

      // Update only the pages in store
      appStore.setDocument({
        ...appStore.document,
        pages,
      })
      if (pages && pages.length) {
        builderStore.setPages(pages)
      }
    } catch (error) {
      handleError(error as AxiosError)
    }
  }

  const setStore = (data: any) => {
    const pages = setFillableFieldsDataIntoDocument(data.pages, data.fillableFields, data.pricingTables) || []
    appStore.setDocument({
      ...data,
      ...(!data.recipients ? { recipients: [] } : {}),
      pages,
    })
    if (pages && pages.length) builderStore.setPages(pages)
    else builderStore.addPage()
    const groups = data?.groups && data?.groups.length ? data?.groups : []
    builderStore.setGroups(groups)
    appStore.setDocFormPublicLink(data.docFormPublicLink)
    builderStore.setIsPublished(data.isPublished)

    if (data?.paymentInfo) {
      const paymentInfo = data.paymentInfo
      const generateInvoiceOnSigning = paymentInfo?.generateInvoiceOnSigning as boolean
      productListStore.toggleGenerateInvoiceOnSigning(generateInvoiceOnSigning)
      if (paymentInfo?.paymentSettings?.schedule?.rrule) {
        const loadedRrule = paymentInfo.paymentSettings.schedule.rrule
        const rruleToSet = generateInvoiceOnSigning ? normalizeRruleForV2Form(loadedRrule) : loadedRrule
        productListStore.setScheduleForMode(rruleToSet, generateInvoiceOnSigning)
        productListStore.setInvoiceType(paymentInfo?.invoiceType)
      }

      if (paymentInfo?.paymentDepositSettings) {
        productListStore.setPaymentDepositSchedule(paymentInfo?.paymentDepositSettings)
      }
      productListStore.toggleEnableAutoPayment(paymentInfo?.enableAutoPayment ?? false)
    }
    const customValueLinkage = data?.customValueLinkage ? data?.customValueLinkage : {}
    builderStore.setCustomValueLinkage(customValueLinkage)
    if (data?.document?.fontsToLoad && data?.document?.fontsToLoad.length > 0 && data?.document?.version > 1) {
      loadFonts(data?.document?.fontsToLoad)
      builderStore.setLoadedFonts(union(builderStore.loadedFonts, data?.document?.fontsToLoad))
    }
  }

  const totalDueAmountOfAllProducts = computed(() => {
    return builderStore.pages
      .reduce((acc, page) => {
        page.children?.forEach(child => {
          if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
            const { amountDue } = useLineItems(child.id)
            acc += amountDue.value
          }
          if (child.type === ELEMENTS_LOOKUP.ROW) {
            child?.children?.forEach(column => {
              column?.children?.forEach(child => {
                if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                  const { amountDue } = useLineItems(child.id)
                  acc += amountDue.value
                }
              })
            })
          }
        })
        return acc
      }, 0)
      ?.toFixed(2)
  })

  const subTotalAcrossProductTables = computed(() => {
    return Number(
      builderStore.pages
        .reduce((acc, page) => {
          page.children?.forEach(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              const { subTotal } = useLineItems(child.id)
              acc += subTotal.value
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              child?.children?.forEach(column => {
                column?.children?.forEach(child => {
                  if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                    const { subTotal } = useLineItems(child.id)
                    acc += subTotal.value
                  }
                })
              })
            }
          })
          return acc
        }, 0)
        ?.toFixed(2)
    )
  })

  const totalDiscountInPercentage = computed(() => {
    // check how many product list has for the document.
    // This is added because of the miscalculation while getting the percentage
    // if it has only one product list and the discount type is in percentage take that value.
    const productLists = builderStore.getAllProductList()
    if (productLists && productLists.length === 1) {
      if (productLists[0].component?.options?.discountType === DiscountType.PERCENTAGE) {
        return productLists[0].component?.options?.discountValue || 0
      }
    }

    // otherwise go ahead with the reduce operation.
    let finalProductListVersion = 1
    const totalDiscountAcrossTables = productLists.reduce((acc, productList) => {
      const { totalDiscount, productListVersion } = useLineItems(productList.id)
      finalProductListVersion = productListVersion.value
      acc += totalDiscount.value
      return acc
    }, 0)
    const { getPercentage } = calculator(Number(subTotalAcrossProductTables.value), finalProductListVersion)
    return getPercentage(totalDiscountAcrossTables, finalProductListVersion)
  })

  const saveDocument = async () => {
    if (!appStore.unsavedChanges) return
    try {
      loading.value = true
      const fillableFields = builderStore.getFillableFields()
      const discounts = productListStore.getEachDiscountsFromProductList()

      const customValueLinkage = builderStore?.customValueLinkage ? builderStore?.customValueLinkage : {}

      const invoiceType = productListStore.invoiceType
      const schedule =
        invoiceType === PriceTypeEnum.RECURRING
          ? {
              rrule: productListStore.generateInvoiceOnSigning ? productListStore.scheduleV2 : productListStore.schedule,
            }
          : {}
      const paymentDepositSettings = productListStore.paymentDepositSchedule || {}
      const paymentInfo = {
        invoiceType,
        schedule,
        generateInvoiceOnSigning: productListStore.generateInvoiceOnSigning,
        paymentDepositSettings,
        enableAutoPayment: productListStore.enableAutoPayment,
      }

      const redirectSettings = appStore?.document?.redirectSettings ? appStore?.document?.redirectSettings : {}

      if (redirectSettings?.documentRedirectUrl) {
        redirectSettings.documentRedirectUrl = /^https?:\/\//i.test(redirectSettings.documentRedirectUrl)
          ? `${redirectSettings.documentRedirectUrl}`
          : `https://${redirectSettings.documentRedirectUrl}`
      }

      if (appStore?.document?.notificationSettings?.value) {
        delete appStore?.document?.notificationSettings?.value
      }

      let contentLibraryThumbnail = ''
      const isContentLibraryTemplate = appStore.document?.type === TemplateTypeEnum.CONTENT_LIBRARY ? true : false
      if (isContentLibraryTemplate) {
        const firstPage = builderStore.pages && builderStore.pages.length > 0 ? builderStore.pages[0] : null
        if (firstPage) {
          contentLibraryThumbnail = (await generateThumbnailForPage(firstPage.id)) as string
        }
      }

      const { data } = await DocumentServices.updateDocument({
        id: route.params.id as string,
        name: appStore.document?.name?.trim(),
        locationId: route.params.locationId as string,
        pages: builderStore.pages,
        groups: builderStore.groups,
        variables: appStore.document?.variables,
        timezone: {
          zone: appStore.timeZone,
          abbreviation: getTimeZoneAbbreviation(appStore.timeZone),
        },
        grandTotal: {
          currency: productListStore.currency || getCurrency(),
          amount: totalDueAmountOfAllProducts.value,
          discountPercentage: totalDiscountInPercentage.value,
          discounts,
        },
        recipients: appStore.document?.recipients?.filter(
          ({ id, entityName }) => id !== ASSIGNED_CONTACT_ID && id !== DOCUMENT_SENDER_ID && entityName !== RecipientEntityTypeEnum.ROLE
        ),
        roles: appStore.document?.roles || [],
        fillableFields: fillableFields,
        pricingTables: builderStore.pages.reduce((acc, page) => {
          page?.children?.forEach(child => {
            if (child.version > 1 && child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              acc.push({
                version: child.version,
                id: child.id,
                lineItems: (child as IElement<ProductListElementOptions>).component.options.lineItems as LineItem[],
              })
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              child?.children?.forEach(column => {
                column?.children?.forEach(child => {
                  if (child.version > 1 && child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                    acc.push({
                      version: child.version,
                      id: child.id,
                      lineItems: (child as IElement<ProductListElementOptions>).component.options.lineItems as LineItem[],
                    })
                  }
                })
              })
            }
          })
          return acc
        }, [] as IPricingTable[]),
        paymentInfo,
        enableDirectPayment: appStore.document?.enableDirectPayment,
        enableAutoSendInvoice: appStore.document?.enableAutoSendInvoice,
        paymentLiveMode: appStore?.document?.paymentLiveMode ?? true,
        customValueLinkage: customValueLinkage,
        notificationSettings: appStore?.document?.notificationSettings ?? {},
        redirectSettings: redirectSettings,
        fontsToLoad: builderStore.fontsToLoad,
        ...(isContentLibraryTemplate && contentLibraryThumbnail && { thumbnail: contentLibraryThumbnail }),
        emailAttachments: appStore.document?.emailAttachments || [],
      } as unknown as UpdateDocument)
      appStore.setDocument({
        ...data,
        pages: builderStore.pages,
      })
      handleSuccess(t('common.templateSavedSuccessfully'))
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
      appStore.setUnsavedChanges(false)
    }
  }

  const removeDocument = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.removeDocument(route.params.id as string)
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  const convertToProposal = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.convertToProposals(
        route.params.locationId as string,
        route.params.id as string,
        appStore.locale || 'en-US'
      )
      pusToProposalBuilder(data._id)
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  function redirectToBuilder(id: string) {
    redirectOnParentRoute({
      name: 'proposals-estimates-template-builder',
      params: { id: id, locationId: route.params.locationId as string },
    })
  }

  const pushToListPage = () => {
    redirectOnParentRoute({
      name: 'proposals-estimates-templates',
      params: {
        locationId: route.params.locationId as string,
      },
    })
  }

  const pusToProposalBuilder = (id: string) => {
    redirectOnParentRoute({
      name: 'builder',
      params: {
        locationId: route.params.locationId as string,
        documentId: id,
      },
    })
  }
  const fetchAllCustomValues = async () => {
    try {
      loadingCustomValues.value = true
      const { data } = await DocumentServices.getAllCustomVariables(route.params.locationId as string, route.params.id as string)
      builderStore.setFlatCustomValues(convertObjToSnakeCase(data, ['document']))
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loadingCustomValues.value = false
    }
  }

  const publishTemplate = async () => {
    try {
      publishingDocument.value = true
      const currentLocale = (locale.value as string) || appStore.locale || 'en-US'
      const { data } = await DocumentServices.publishDocForm(
        route.params.locationId as string,
        route.params.id as string,
        true,
        currentLocale
      )
      appStore.setDocument({
        ...appStore.document,
        isPublished: data?.isPublished,
        publishedAt: data?.publishedAt,
      })
      appStore.setDocFormPublicLink(data?.url)
      builderStore.setIsPublished(data.isPublished)
      handleSuccess(t('common.templatePublishedSuccessfully'))
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      publishingDocument.value = false
    }
  }

  const unpublishTemplate = async () => {
    try {
      publishingDocument.value = true
      const { data } = await DocumentServices.unpublishDocForm(route.params.locationId as string, route.params.id as string)
      appStore.setDocument({
        ...appStore.document,
        isPublished: data?.isPublished,
        publishedAt: null as unknown as Date,
      })
      builderStore.setIsPublished(data.isPublished)
      appStore.setDocFormPublicLink('')
      handleSuccess(t('common.templateUnpublishedSuccessfully'))
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      publishingDocument.value = false
    }
  }

  const getContentLibraryCustomContentCount = async () => {
    try {
      const { data } = await DocumentServices.getContentLibraryCustomContentCount({
        locationId: route.params.locationId as string,
      })
      contentLibraryCustomContentCount.value = data.customCount || 0
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    }
  }

  return {
    loading,
    saveDocument,
    validateDocument,
    loadDocument,
    updatePagesAfterUpload,
    loadTemplate,
    removeDocument,
    pushToListPage,
    redirectToBuilder,
    convertToProposal,
    fetchAllCustomValues,
    loadingCustomValues,
    publishTemplate,
    unpublishTemplate,
    publishingDocument,
    totalDueAmountOfAllProducts,
    documentVersion,
    getContentLibraryCustomContentCount,
    contentLibraryCustomContentCount,
  }
}
